import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
  RefObject,
} from 'react';

import { useIsContentOverflowing } from '@adsk/alloy-react-helpers';
import { ChevronRightIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import Tag from '@adsk/alloy-react-tag';
import { StylableComponent, StyleProp } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { isGroupHeader } from '../../helpers';
import { defaultRenderGroupHeader } from '../../helpers/defaultRenderers';
import { useTranslation } from '../../i18n';
import { GenericItem, GroupHeader, RenderGroupHeaderProps } from '../../types';

type GroupFilterProps<TItem extends GenericItem> =
  StylableComponent<HTMLButtonElement> & {
    menuItems: TItem[];
    selectedGroupKey?: string;
    setSelectedGroupKey?: (key: string) => void;
    renderGroupHeader?: (props: RenderGroupHeaderProps) => React.ReactNode;
    showGroupItemCount?: boolean;
    isAutocomplete?: boolean;
    setHighlightedIndex?: (index: number) => void;
    refToFocusOnClick?: RefObject<HTMLElement>;
    dropdownId?: string;
  };

const TOKEN_HEIGHT = 26;
const CONTENT_OVERFLOW_OPTIONS = {};

const GroupFilter = <TItem extends GenericItem>({
  style = {},
  menuItems,
  selectedGroupKey,
  setSelectedGroupKey,
  renderGroupHeader = defaultRenderGroupHeader,
  showGroupItemCount,
  isAutocomplete,
  setHighlightedIndex,
  refToFocusOnClick,
  dropdownId,
}: GroupFilterProps<TItem>) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const groupFilterTokens = tokens?.component.dropdown?.menu?.groupFilter;
  const tokenRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useIsContentOverflowing(
    scrollRef,
    CONTENT_OVERFLOW_OPTIONS,
  );
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const updateScrollEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const lastChild = el.lastElementChild as HTMLElement | null;
    setScrolledToEnd(
      !lastChild ||
        lastChild.getBoundingClientRect().left <
          el.getBoundingClientRect().right,
    );
  }, []);

  const tokenClickHandler = useCallback(
    (key: string, headerIndex: number) => {
      setSelectedGroupKey?.(key);
      setHighlightedIndex?.(headerIndex);
      refToFocusOnClick?.current?.focus?.();
    },
    [setSelectedGroupKey, setHighlightedIndex, refToFocusOnClick],
  );

  const getTargetProps = useCallback(
    () => ({
      style: { minWidth: 0, maxWidth: '21ch' },
    }),
    [],
  );

  const chipKey = JSON.stringify(groupFilterTokens?.chip);
  const chipStyle = useMemo<StyleProp>(
    () => ({
      ...groupFilterTokens?.chip,
      height: TOKEN_HEIGHT,
      textWrap: 'nowrap',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chipKey],
  );

  const items = useMemo(() => {
    let cumulativeIndex = 0;

    return menuItems.flatMap((item) => {
      const index = cumulativeIndex;
      if (isGroupHeader?.(item)) {
        const groupHeader = item as GroupHeader;
        if (groupHeader.pinned) {
          cumulativeIndex += groupHeader.itemCount;
          return [];
        }

        const token = (
          <div
            key={groupHeader.groupKey}
            ref={(el) => {
              tokenRefs.current[groupHeader.groupKey] = el;
            }}
          >
            <Tag
              selected={groupHeader.groupKey === selectedGroupKey}
              onChange={() => {
                tokenClickHandler(groupHeader.groupKey, index - 1);
              }}
              disabled={(groupHeader.filteredItemCount ?? 0) <= 0}
              style={chipStyle}
            >
              <OverflowTooltip
                singleLine
                autoTipOnEllipsis
                getTargetProps={getTargetProps}
              >
                {groupHeader.label}
              </OverflowTooltip>
            </Tag>
          </div>
        );
        cumulativeIndex += groupHeader.itemCount;

        return [token];
      }

      return [];
    });
  }, [
    chipStyle,
    getTargetProps,
    menuItems,
    selectedGroupKey,
    tokenClickHandler,
  ]);

  useEffect(() => {
    if (selectedGroupKey && tokenRefs?.current[selectedGroupKey]) {
      tokenRefs.current[selectedGroupKey]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateScrollEnd();
  }, [hasOverflow, updateScrollEnd]);

  const selectedGroupItem = useMemo(
    () =>
      menuItems.find(
        (item) => item.groupKey && item.groupKey === selectedGroupKey,
      ) as unknown as GroupHeader,
    [menuItems, selectedGroupKey],
  );
  const hasPinnedGroups = useMemo(
    () =>
      menuItems.some(
        (item) => isGroupHeader?.(item) && (item as GroupHeader).pinned,
      ),
    [menuItems],
  );

  if (items?.length <= 1) {
    return null;
  }

  return (
    <>
      <div
        css={[groupFilterTokens?.container, style]}
        data-testid="group-filter"
        id={dropdownId ? `${dropdownId}-group-filter` : undefined}
      >
        <Tag
          selected={!selectedGroupKey}
          onChange={() => {
            tokenClickHandler('', -1);
          }}
          style={chipStyle}
        >
          {t('ALL')}
        </Tag>
        <span css={groupFilterTokens?.divider} />
        <div
          ref={scrollRef}
          onScroll={updateScrollEnd}
          data-testid="group-filter-scroll"
          css={[
            {
              display: 'flex',
              overflowX: 'auto',
            },
          ]}
        >
          {items}
        </div>
        {hasOverflow && (
          <span
            css={groupFilterTokens?.chevron}
            style={{ opacity: scrolledToEnd ? 0 : 1 }}
            data-testid="group-filter-chevron"
            aria-hidden
          >
            <ChevronRightIcon size={16} />
          </span>
        )}
      </div>
      {selectedGroupItem && !hasPinnedGroups && (
        <div css={[groupFilterTokens?.selectedHeader, style]}>
          {renderGroupHeader?.({
            item: selectedGroupItem,
            index: 0,
            showGroupItemCount,
            isAutocomplete,
          })}
        </div>
      )}
    </>
  );
};

export default GroupFilter;
